import React, { ReactNode } from 'react';
import { PropGetters } from 'downshift';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { useFloating } from '@floating-ui/react';
import { ITheme, themed } from '../../../themer';
import { SelectCurrent } from './SelectCurrent';
import { SelectPlaceholder } from './SelectPlaceholder';
import { SelectToggleIcon, StyledSelectToggleIcon } from './SelectToggleIcon';
import {
	DropdownItem,
	IDropdownItem,
	IGetItemDisplayValue,
	StyledDropdownItem,
} from '../../DropdownList';
import { Css } from '../../../utils';
import { Color } from '../../../../styles/Color';

interface IStyledSelectMainProps {
	disabled?: boolean;
	invalid?: boolean;
	hasFloatingLabelStyle?: boolean;
	className?: string;
}

export const StyledSelectMain = styled.div<IStyledSelectMainProps, ITheme>`
	display: flex;
	position: relative;
	padding: ${Css.pxToRem(16)};
	max-height: ${Css.pxToRem(56)};
	background: ${themed(({ select }, { disabled }) =>
		disabled ? select.disabled.background : select.main.background,
	)};
	border-radius: ${themed(({ select }) => select.main.borderRadius)};
	color: ${themed(({ select }, { disabled }) =>
		disabled ? select.disabled.color : select.main.color,
	)};
	font-size: ${themed(({ select }) => select.main.fontSize)};
	cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
	border-bottom: ${themed(({ select }) => select.main.borderBottom)};

	${SelectCurrent} {
		flex-grow: 1;
	}

	${({ invalid }) =>
		invalid &&
		css`
			color: ${Color.Error};
			border-bottom: 2px solid ${Color.Error};
			path {
				fill: ${Color.Error};
			}

			${SelectPlaceholder} {
				color: ${Color.Error};
			}

			${StyledSelectToggleIcon} {
				path {
					fill: ${Color.Error};
				}
			}
		`};

	${({ hasFloatingLabelStyle, theme: { input } }) =>
		hasFloatingLabelStyle &&
		// Shift 4 px
		css`
			padding-bottom: ${Css.calc(
				'14px',
				// We need to subtract 4px + 2px extra because 4px padding top on DropdownItem causes the component to grow with 2px
				(value) => value - 2,
			)};

			${StyledDropdownItem} {
				padding-top: 4px;
				width: max-content;
			}

			${SelectCurrent} {
				padding-top: calc(${input.padding} / 4);
			}

			${SelectPlaceholder} {
				padding-top: calc(${input.padding} / 4);
				font-size: 13px;
			}
		`}
`;

const SelectButtonWrapper = styled.button`
	background: none;
	border: none;
	padding: 0;
	font-family: inherit;
	text-decoration: none;
`;

export const SelectInput = styled.input<{}, ITheme>`
	position: absolute;
	left: -9999px;
	width: 100%;
	height: 1px;

	&:focus + ${StyledSelectMain}, & + ${StyledSelectMain}.is-open {
		background: ${themed(({ select }) => select.focus.background)};
		color: ${themed(({ select }) => select.focus.color)};
		box-shadow: ${themed(({ select }) => select.focus.boxShadow)};
		border-bottom: ${themed(({ select }) => select.focus.borderBottom)};
		border-radius: ${themed(({ select }) => select.focus.borderRadius)};
	}
`;

export const SelectFloatingPlaceholder = styled.label<
	{ hasValue: boolean; hasIcon: boolean; isOpen: boolean },
	ITheme
>`
	position: absolute;
	top: 0;
	left: 0;
	opacity: 0;
	color: ${Color.Neutral6};

	${({ hasIcon }) =>
		hasIcon &&
		css`
			margin-left: ${Css.pxToRem(34)};
		`}

	${({ hasValue, theme: { input } }) =>
		hasValue &&
		css`
			opacity: 1;
			padding: calc(${input.padding} / 3) ${input.padding};
			font-size: ${Css.pxToRem(12)};
			font-weight: 500;
			margin-top: ${Css.pxToRem(4)};
		`}
`;

const SelectIcon = styled.div<{}, ITheme>`
	margin-right: ${Css.pxToRem(8)};
	display: flex;
	align-items: center;
	path {
		fill: ${(props) => props.theme.select.main.color};
	}
`;

interface ISelectMainProps {
	getInputProps: PropGetters<IDropdownItem>['getInputProps'];
	getToggleButtonProps: PropGetters<IDropdownItem>['getToggleButtonProps'];
	getItemDisplayValue: IGetItemDisplayValue;
	selectedItem: IDropdownItem | null;
	placeholder?: string;
	ariaLabel?: string;
	disabled?: boolean;
	invalid?: boolean;
	isOpen: boolean;
	icon?: ReactNode;
	placeholderAsLabel?: boolean;
	floatingRefs?: ReturnType<typeof useFloating>['refs'];
	onClick?: () => void;
	wrapSelectForHorizontalScroll?: boolean;
}

export function SelectMain({
	getInputProps,
	getToggleButtonProps,
	selectedItem,
	placeholder,
	ariaLabel,
	getItemDisplayValue,
	isOpen,
	disabled,
	invalid,
	icon,
	placeholderAsLabel,
	floatingRefs,
	onClick,
	wrapSelectForHorizontalScroll,
}: ISelectMainProps) {
	// TO DO: This component needs refactoring https://moveagency.atlassian.net/browse/HAS-917

	const hint =
		selectedItem && typeof selectedItem === 'object'
			? selectedItem.hint
			: undefined;
	const mainProps = disabled ? {} : getToggleButtonProps();

	const renderSelectMainComponent = () => (
		<StyledSelectMain
			{...mainProps}
			hasFloatingLabelStyle={selectedItem && placeholderAsLabel}
			disabled={disabled}
			invalid={invalid}
			className={isOpen ? 'is-open' : ''}
		>
			{icon && <SelectIcon>{icon}</SelectIcon>}
			<SelectCurrent>
				{placeholderAsLabel && (
					<SelectFloatingPlaceholder
						isOpen={isOpen}
						hasIcon={!!icon}
						hasValue={!!selectedItem}
					>
						{placeholder}
					</SelectFloatingPlaceholder>
				)}
				{selectedItem ? (
					<DropdownItem
						displayValue={getItemDisplayValue(selectedItem)}
						hint={hint}
					/>
				) : (
					<SelectPlaceholder>{placeholder}</SelectPlaceholder>
				)}
			</SelectCurrent>
			{!disabled && <SelectToggleIcon isOpen={isOpen} />}
		</StyledSelectMain>
	);

	if (wrapSelectForHorizontalScroll) {
		return (
			<SelectButtonWrapper
				type="button"
				ref={floatingRefs?.setReference}
				id="select-main"
				aria-expanded={isOpen}
				aria-controls={isOpen ? 'select-list' : undefined}
				onClick={onClick}
			>
				{renderSelectMainComponent()}
			</SelectButtonWrapper>
		);
	}

	return (
		<>
			<SelectInput
				{...getInputProps({ disabled })}
				aria-labelledby={undefined}
				aria-label={ariaLabel}
				aria-invalid={invalid}
			/>

			{renderSelectMainComponent()}
		</>
	);
}
